// Il canale cifrato verso Phantom, da telefono.
//
// COSA È VIVO E COSA NO
//
//   `connetti()` / urlConnessione  → IN USO. È così che il wallet si collega
//   dal browser normale: Phantom si apre come app, l'utente approva, si torna
//   qui con l'indirizzo. Questa tappa ha sempre funzionato.
//
//   `richiediFirma()` / urlFirma   → NON IN USO. Phantom rifiuta la firma con
//   `-32603 Unexpected error`, un guasto interno loro aperto dal 2024. La firma
//   passa oggi dal suo browser interno, e il token torna indietro con la
//   consegna della sessione (lib/consegnaSessione.js). L'elenco completo di
//   ciò che abbiamo escluso — e che non va rifatto — sta nella roadmap, Fase B
//   punto 5d.
//
//   Il codice della firma resta perché è provato e perché servirà: i depositi
//   da telefono (roadmap Fase D punto 18) passano dallo stesso protocollo, e il
//   giorno in cui Phantom sistemerà il rifiuto è già pronto.
//
// COME FUNZIONA IL CANALE
//
// Phantom non risponde alla pagina: risponde all'URL. Le risposte viaggiano
// quindi nella barra degli indirizzi, dove finirebbero nella cronologia, nei
// log e nel Referer. Per questo il contenuto è cifrato: all'inizio generiamo
// una coppia di chiavi x25519 usa e getta, Phantom ci manda la sua parte
// pubblica, e da lì in poi ogni scambio passa dentro una scatola NaCl che solo
// noi due possiamo aprire. Nell'URL viaggia solo testo illeggibile.
//
// La chiave segreta va conservata tra un passaggio e l'altro, perché la pagina
// viene ricaricata da capo al ritorno: sta in localStorage e non è la chiave
// del wallet — cifra solo questo canale, e si butta al termine.
//
// IL GIRO COMPLETO, IN DUE TAPPE
//
//   1. connetti()      → Phantom si apre, l'utente approva → torniamo con
//                        l'indirizzo del wallet e una `session`
//   2. richiediFirma() → Phantom si riapre con il messaggio di sfida da
//                        firmare → torniamo con la firma
//
// Due approvazioni sono il minimo consentito dal protocollo: la prima autorizza
// il collegamento, la seconda dimostra il possesso della chiave. Sul desktop
// non si vedono perché l'estensione tiene aperta la stessa pagina.

import nacl from 'tweetnacl';
import bs58 from 'bs58';

const CHIAVE_SEGRETO = 'vexel_phantom_dapp_sk';
const CHIAVE_SESSIONE = 'vexel_phantom_session';
const CHIAVE_WALLET = 'vexel_phantom_wallet';
const CHIAVE_CONDIVISO = 'vexel_phantom_shared';

// Quale tappa stiamo aspettando. Sta QUI e non nell'indirizzo di ritorno per
// un motivo preciso: `redirect_link` viene registrato da Phantom nella sessione
// e riusato per la gestione delle app attendibili. Mandargliene due diversi —
// uno per la connessione, uno per la firma — significa chiedergli di fidarsi di
// un indirizzo e poi presentarsene un altro. Il ritorno da Phantom si riconosce
// benissimo dai parametri che accoda lui, senza aggiungerne di nostri.
const CHIAVE_ATTESA = 'vexel_phantom_attesa';

function leggi(chiave) {
  try { return localStorage.getItem(chiave); } catch (e) { return null; }
}

// Rifiuta tutto cio' che non e' una stringa piena. Senza questo controllo un
// `undefined` diventa la STRINGA "undefined" dentro localStorage: truthy, quindi
// indistinguibile da un valore buono per qualunque guardia, e spedita a Phantom
// come se fosse una sessione vera. Il guasto si manifesta due schermate dopo,
// come errore interno, e non porta alcuna traccia della sua origine.
function scrivi(chiave, valore) {
  if (typeof valore !== 'string' || valore === '') {
    throw new Error(`Phantom returned no ${chiave}`);
  }
  try { localStorage.setItem(chiave, valore); } catch (e) {}
}

function cancella(chiave) {
  try { localStorage.removeItem(chiave); } catch (e) {}
}

// Coppia di chiavi del canale. Viene creata alla prima connessione e riusata
// per tutta la sessione: rigenerarla a metà strada renderebbe indecifrabile la
// risposta di Phantom, che sta già cifrando per la chiave precedente.
function coppiaDiChiavi() {
  const salvata = leggi(CHIAVE_SEGRETO);
  if (salvata) {
    try {
      return nacl.box.keyPair.fromSecretKey(bs58.decode(salvata));
    } catch (e) {
      // Valore corrotto: meglio ricominciare che restare bloccati.
      cancella(CHIAVE_SEGRETO);
    }
  }
  const nuova = nacl.box.keyPair();
  scrivi(CHIAVE_SEGRETO, bs58.encode(nuova.secretKey));
  return nuova;
}

function segretoCondiviso() {
  const s = leggi(CHIAVE_CONDIVISO);
  return s ? bs58.decode(s) : null;
}

// Dove Phantom ci deve rimandare: sempre lo stesso indirizzo, pulito, identico
// per entrambe le tappe. Nessuna query nostra.
function indirizzoDiRitorno() {
  return window.location.origin + window.location.pathname;
}

export function walletCollegato() {
  return leggi(CHIAVE_WALLET);
}

export function scollega() {
  [CHIAVE_SEGRETO, CHIAVE_SESSIONE, CHIAVE_WALLET, CHIAVE_CONDIVISO, CHIAVE_ATTESA].forEach(cancella);
}

// --- Tappa 1: collegamento -------------------------------------------------

// Il calcolo dell'indirizzo e il salto sono separati: il primo e' pura
// aritmetica e si puo' verificare, il secondo e' una riga che nessun test puo'
// eseguire senza un browser vero.
// `cluster` e' OPZIONALE, e di proposito non lo mandiamo.
//
// Phantom lega la sessione al cluster dichiarato qui. Chiedendo `devnet` a
// un'app che non ha il Testnet Mode acceso — che e' l'impostazione predefinita
// per chiunque — si ottiene una sessione agganciata a una rete su cui quell'app
// non puo' operare: la connessione riesce, e tutto il resto fallisce con un
// errore interno generico. E' la spiegazione piu' probabile del `-32603` che ci
// ha bloccati, e non a caso il gioco avverte gia' dello stesso disallineo per i
// depositi.
//
// Di questa sessione ci serve SOLO la firma di un messaggio, che e' off-chain e
// non ha nessun rapporto con la rete. Chiedere un cluster sarebbe stato inutile
// anche se avesse funzionato.
//
// ATTENZIONE per il futuro: il giorno in cui questa sessione servisse anche a
// firmare TRANSAZIONI (depositi da telefono, roadmap Fase D punto 18) il
// cluster torna obbligatorio e va fatto combaciare con quello del server —
// altrimenti si firmano transazioni su una rete e il server le cerca su
// un'altra, cioe' soldi che partono e non arrivano.
export function urlConnessione(cluster) {
  const coppia = coppiaDiChiavi();
  const params = new URLSearchParams({
    dapp_encryption_public_key: bs58.encode(coppia.publicKey),
    app_url: window.location.origin,
    redirect_link: indirizzoDiRitorno(),
  });
  if (cluster) params.set('cluster', cluster);
  return `https://phantom.app/ul/v1/connect?${params.toString()}`;
}

export function connetti(cluster) {
  const url = urlConnessione(cluster);
  // Il marcatore registra una PARTENZA, non il calcolo di un indirizzo: le
  // funzioni url*() vengono chiamate anche solo per misurare la richiesta, a
  // ogni render, e scrivere li' dentro falserebbe la tappa attesa.
  scrivi(CHIAVE_ATTESA, 'connect');
  window.location.href = url;
}

// --- Tappa 2: firma del messaggio di sfida ---------------------------------

export function urlFirma(messaggio) {
  const condiviso = segretoCondiviso();
  const sessione = leggi(CHIAVE_SESSIONE);
  // "undefined" e "null" sono i due modi in cui un valore mancante sopravvive a
  // un passaggio da localStorage travestito da stringa buona.
  const sessioneValida = typeof sessione === 'string' && sessione !== ''
    && sessione !== 'undefined' && sessione !== 'null';
  if (!condiviso || !sessioneValida) {
    throw new Error('Phantom session missing or invalid: disconnect and reconnect');
  }

  const coppia = coppiaDiChiavi();
  const nonce = nacl.randomBytes(24);
  // ESATTAMENTE questi due campi, nient'altro.
  //
  // La documentazione dà `display` per opzionale, ma l'app dimostrativa
  // ufficiale di Phantom (github.com/phantom/deep-link-demo-app) non lo manda:
  // il suo payload è `{ session, message }` e basta. Aggiungerne un terzo è
  // l'unica differenza strutturale rimasta fra la nostra richiesta e la loro, e
  // `-32603` è l'errore che ci si aspetta da un lettore severo che incontra un
  // campo inatteso. Non aggiungere campi qui senza averli visti in quel file.
  const corpo = {
    session: sessione,
    // Phantom vuole il messaggio in base58, non in testo semplice.
    message: bs58.encode(new TextEncoder().encode(messaggio)),
  };
  const cifrato = nacl.box.after(
    new TextEncoder().encode(JSON.stringify(corpo)),
    nonce,
    condiviso
  );

  const params = new URLSearchParams({
    dapp_encryption_public_key: bs58.encode(coppia.publicKey),
    nonce: bs58.encode(nonce),
    redirect_link: indirizzoDiRitorno(),
    payload: bs58.encode(cifrato),
  });
  return `https://phantom.app/ul/v1/signMessage?${params.toString()}`;
}

export function richiediFirma(messaggio) {
  const url = urlFirma(messaggio);
  scrivi(CHIAVE_ATTESA, 'sign');
  window.location.href = url;
}

// Le misure della richiesta, senza spedirla. Servono a distinguere a occhio un
// guasto di protocollo da un guasto di dimensione: se Phantom rifiuta una
// richiesta di 900 caratteri e ne accetta una di 300, il problema e' quanto le
// stiamo mandando, non come. Non espone nessun valore, solo lunghezze.
export function misureRichiesta(messaggio) {
  const sessione = leggi(CHIAVE_SESSIONE);
  const url = urlFirma(messaggio);
  const params = new URL(url).searchParams;
  return {
    sessione: sessione ? sessione.length : 0,
    payload: (params.get('payload') || '').length,
    url: url.length,
  };
}

// --- Il ritorno ------------------------------------------------------------

function apriScatola(dataB58, nonceB58, condiviso) {
  const aperto = nacl.box.open.after(
    bs58.decode(dataB58),
    bs58.decode(nonceB58),
    condiviso
  );
  if (!aperto) throw new Error('Phantom reply could not be decrypted');
  return JSON.parse(new TextDecoder().decode(aperto));
}

// Toglie dalla barra degli indirizzi i parametri della risposta. Non è
// cosmetica: senza, un ricaricamento della pagina rieseguirebbe il ritorno con
// dati ormai consumati, e la cronologia conserverebbe la firma.
function pulisciIndirizzo() {
  try {
    window.history.replaceState({}, '', window.location.origin + window.location.pathname);
  } catch (e) {}
}

// Va chiamata al caricamento della pagina. Ritorna null se non stiamo tornando
// da Phantom; altrimenti dice quale tappa si è conclusa e con quale esito.
export function gestisciRitorno() {
  const params = new URLSearchParams(window.location.search);

  // Un ritorno da Phantom si riconosce dai parametri che accoda LUI. Non ne
  // serve uno nostro nell'indirizzo, e non averlo tiene `redirect_link`
  // identico tra connessione e firma — che e' l'indirizzo con cui Phantom ha
  // registrato l'app nella sessione.
  const chiavePubblicaPhantom = params.get('phantom_encryption_public_key');
  const dati = params.get('data');
  const errore = params.get('errorMessage');
  if (!chiavePubblicaPhantom && !dati && !errore) return null;

  // Solo la connessione restituisce la chiave pubblica di Phantom: e' il modo
  // piu' affidabile di distinguere le due tappe, perche' viene dai dati e non
  // da una nostra annotazione che potrebbe essere stata persa.
  const attesa = leggi(CHIAVE_ATTESA);
  const tappa = chiavePubblicaPhantom ? 'connect' : (attesa || 'sign');
  cancella(CHIAVE_ATTESA);

  // I NOMI dei parametri tornati, non i valori: dicono se Phantom ha risposto
  // con un errore, con dei dati, o con niente del tutto — che sono tre guasti
  // diversi — senza mettere la firma sotto gli occhi di nessuno.
  const ricevuti = Array.from(params.keys()).join(',') || 'nessuno';

  if (errore) {
    const codice = params.get('errorCode');
    pulisciIndirizzo();
    return codice ? { tappa, errore, codice, ricevuti } : { tappa, errore, ricevuti };
  }

  try {
    if (tappa === 'connect') {
      const coppia = coppiaDiChiavi();
      const condiviso = nacl.box.before(
        bs58.decode(chiavePubblicaPhantom),
        coppia.secretKey
      );
      const contenuto = apriScatola(dati, params.get('nonce'), condiviso);

      // Se i campi non sono quelli attesi, e' qui che va detto: piu' avanti
      // l'unica traccia sarebbe un rifiuto generico di Phantom. I nomi dei
      // campi ricevuti bastano a capirlo e non espongono nulla di sensibile.
      if (typeof contenuto.session !== 'string' || typeof contenuto.public_key !== 'string') {
        throw new Error(`risposta inattesa dalla connessione (campi: ${Object.keys(contenuto).join(',')})`);
      }

      scrivi(CHIAVE_CONDIVISO, bs58.encode(condiviso));
      scrivi(CHIAVE_SESSIONE, contenuto.session);
      scrivi(CHIAVE_WALLET, contenuto.public_key);
      pulisciIndirizzo();
      return { tappa, wallet: contenuto.public_key, ricevuti };
    }

    const condiviso = segretoCondiviso();
    if (!condiviso) throw new Error('Phantom session lost');
    const contenuto = apriScatola(dati, params.get('nonce'), condiviso);
    pulisciIndirizzo();
    // La firma torna in base58: e' gia' la forma che il server si aspetta.
    return { tappa: 'sign', firma: contenuto.signature, wallet: walletCollegato(), ricevuti };
  } catch (e) {
    pulisciIndirizzo();
    return { tappa, errore: e.message, ricevuti };
  }
}
